import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiSend, FiXCircle, FiLock, FiUnlock, FiRotateCcw, FiCheckCircle, FiDownload } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { Auth } from 'aws-amplify';
import Loading from '../../ions/Loading';
import { signatureApi, ApiError } from '../../../services/signatureApi';
import { SignatureProcessDetail, SignerDetail, ObserverDetail, DocumentDetail } from './types';

const getCurrentUser = async (): Promise<string> => {
    try {
        const session = await Auth.currentSession();
        return session.getIdToken().payload.email || session.getIdToken().payload.sub || 'unknown';
    } catch {
        return 'unknown';
    }
};

const STATUS_COLORS: Record<string, string> = {
    DRAFT: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
    SENT: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
    IN_PROGRESS: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300',
    COMPLETED: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
    CANCELLED: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
    REJECTED: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
    EXPIRED: 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300',
    FROZEN: 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300',
    ERROR: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
    CLOSED: 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
    PENDING: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300',
    SIGNED: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
};

const STATUS_LABELS: Record<string, string> = {
    DRAFT: 'Rascunho', SENT: 'Enviado', IN_PROGRESS: 'Em andamento', COMPLETED: 'Concluído',
    CANCELLED: 'Cancelado', REJECTED: 'Rejeitado', EXPIRED: 'Expirado', FROZEN: 'Congelado',
    ERROR: 'Erro', CLOSED: 'Encerrado', PENDING: 'Pendente', SIGNED: 'Assinou',
};

const formatDate = (v: string | null) => v ? new Date(v).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';

const formatCpfCnpj = (value: string | null | undefined): string => {
    if (!value) return '';
    const digits = value.replace(/\D/g, '');
    if (digits.length === 11) return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    if (digits.length === 14) return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    return value;
};

const SignatureDetailPage = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [data, setData] = useState<SignatureProcessDetail | null>(null);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [cancelReason, setCancelReason] = useState('');
    const [showCancelModal, setShowCancelModal] = useState(false);

    const fetchDetail = useCallback(async () => {
        if (!id) return;
        setLoading(true);
        try {
            const result = await signatureApi.getProcessDetail(id);
            setData(result.data || result);
        } catch (e: any) {
            if (e instanceof ApiError) {
                if (e.status === 403) return navigate('/denied');
                if (e.status === 404) return navigate('/not-found');
            }
            toast.error('Erro ao carregar detalhes');
        } finally {
            setLoading(false);
        }
    }, [id, navigate]);

    useEffect(() => { fetchDetail(); }, [fetchDetail]);

    const doAction = async (action: () => Promise<any>, msg: string) => {
        setActionLoading(true);
        try { await action(); toast.success(msg); fetchDetail(); }
        catch (e: any) { toast.error(e.message || 'Erro'); }
        finally { setActionLoading(false); }
    };

    if (loading) return <Loading />;
    if (!data) return <div className="p-6 text-center text-gray-400 dark:text-gray-500">Processo não encontrado</div>;

    const s = data.status;
    const canSend = s === 'DRAFT';
    const canRetry = s === 'ERROR';
    const canCancel = ['DRAFT', 'ERROR', 'SENT', 'IN_PROGRESS', 'FROZEN'].includes(s);
    const canComplete = ['COMPLETED', 'EXPIRED', 'CANCELLED', 'REJECTED'].includes(s);
    const canFreeze = s === 'DRAFT';
    const canRetrieve = ['SENT', 'IN_PROGRESS'].includes(s);

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-slate-800">
            {actionLoading && <Loading />}

            {/* Header */}
            <div className="bg-white dark:bg-slate-900 border-b border-gray-200 dark:border-slate-700 px-6 py-4">
                <div className="flex items-center gap-3 max-w-5xl mx-auto">
                    <button onClick={() => navigate('/signature')} className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg">
                        <FiArrowLeft className="text-gray-600 dark:text-gray-400" />
                    </button>
                    <div className="flex-1">
                        <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Processo de Assinatura</h1>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Operação: {data.operationNumber || '-'}</p>
                    </div>
                    <span className={`px-3 py-1 text-sm font-medium rounded-full ${STATUS_COLORS[s] || ''}`}>
                        {STATUS_LABELS[s] || s}
                    </span>
                </div>
            </div>

            <div className="max-w-5xl mx-auto px-6 py-6 space-y-6">
                {/* Info cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[
                        { label: 'Cliente', value: data.personName || '-', sub: formatCpfCnpj(data.personGovTaxId) },
                        { label: 'Valor', value: data.operationValue ? `R$ ${Number(data.operationValue).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : '-' },
                        { label: 'Criado em', value: formatDate(data.createdAt) },
                        { label: 'Atualizado em', value: formatDate(data.updatedAt) },
                    ].map((c, i) => (
                        <div key={i} className="bg-white dark:bg-slate-900 rounded-lg border border-gray-200 dark:border-slate-700 p-4">
                            <p className="text-xs text-gray-500 dark:text-gray-400">{c.label}</p>
                            <p className="text-sm font-medium text-gray-900 dark:text-white">{c.value}</p>
                            {c.sub && <p className="text-xs text-gray-400 dark:text-gray-500">{c.sub}</p>}
                        </div>
                    ))}
                </div>

                {/* Alerts */}
                {data.errorDetail && (
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                        <p className="text-sm text-red-700 dark:text-red-300"><strong>Erro:</strong> {data.errorDetail}</p>
                    </div>
                )}
                {data.cancelReason && (
                    <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4">
                        <p className="text-sm text-orange-700 dark:text-orange-300"><strong>Cancelamento:</strong> {data.cancelReason}</p>
                    </div>
                )}

                {/* Actions */}
                <div className="flex flex-wrap gap-2">
                    {canSend && <button disabled={actionLoading} onClick={() => doAction(() => signatureApi.sendProcess(id!), 'Enviado')}
                        className="flex items-center gap-1 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"><FiSend /> Enviar</button>}
                    {canRetry && <button disabled={actionLoading} onClick={() => doAction(async () => signatureApi.retryProcess(id!, await getCurrentUser()), 'Reenviado')}
                        className="flex items-center gap-1 px-4 py-2 text-sm bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 transition-colors"><FiRotateCcw /> Tentar Novamente</button>}
                    {canFreeze && <button disabled={actionLoading} onClick={() => doAction(async () => signatureApi.freezeProcess(id!, 'Pendência cadastral', await getCurrentUser()), 'Congelado')}
                        className="flex items-center gap-1 px-4 py-2 text-sm bg-purple-500 text-white rounded-lg hover:bg-purple-600 disabled:opacity-50 transition-colors"><FiLock /> Congelar</button>}
                    {s === 'FROZEN' && <button disabled={actionLoading} onClick={() => doAction(() => signatureApi.unfreezeProcess(id!), 'Descongelado')}
                        className="flex items-center gap-1 px-4 py-2 text-sm bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 transition-colors"><FiUnlock /> Descongelar</button>}
                    {canRetrieve && <button disabled={actionLoading} onClick={() => doAction(async () => signatureApi.retrieveProcess(id!, 'Alteração', await getCurrentUser()), 'Resgatado')}
                        className="flex items-center gap-1 px-4 py-2 text-sm border border-gray-300 dark:border-slate-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 text-gray-700 dark:text-gray-300 disabled:opacity-50 transition-colors"><FiRotateCcw /> Resgatar</button>}
                    {canComplete && <button disabled={actionLoading} onClick={() => doAction(async () => signatureApi.completeProcess(id!, 'MANUAL', await getCurrentUser()), 'Encerrado')}
                        className="flex items-center gap-1 px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"><FiCheckCircle /> Encerrar</button>}
                    {canCancel && <button onClick={() => setShowCancelModal(true)}
                        className="flex items-center gap-1 px-4 py-2 text-sm bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"><FiXCircle /> Cancelar</button>}
                </div>

                {/* Signers */}
                <div className="bg-white dark:bg-slate-900 rounded-lg border border-gray-200 dark:border-slate-700">
                    <h2 className="px-4 py-3 text-sm font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-slate-700">
                        Signatários ({data.signers?.length || 0})
                    </h2>
                    <table className="w-full text-left">
                        <thead className="bg-gray-50 dark:bg-slate-800"><tr>
                            <th className="px-4 py-2 text-xs font-semibold text-gray-600 dark:text-gray-400">Nome</th>
                            <th className="px-4 py-2 text-xs font-semibold text-gray-600 dark:text-gray-400">Email</th>
                            <th className="px-4 py-2 text-xs font-semibold text-gray-600 dark:text-gray-400">Papel</th>
                            <th className="px-4 py-2 text-xs font-semibold text-gray-600 dark:text-gray-400">Status</th>
                            <th className="px-4 py-2 text-xs font-semibold text-gray-600 dark:text-gray-400">Assinado em</th>
                            <th className="px-4 py-2 text-xs font-semibold text-gray-600 dark:text-gray-400">Ações</th>
                        </tr></thead>
                        <tbody>
                            {(data.signers || []).map((sg: SignerDetail) => (
                                <tr key={sg.signerId} className="border-b border-gray-200 dark:border-slate-700">
                                    <td className="px-4 py-2 text-sm font-medium text-gray-900 dark:text-white">{sg.name}</td>
                                    <td className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">{sg.email || '-'}</td>
                                    <td className="px-4 py-2"><span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded text-xs">{sg.role}</span></td>
                                    <td className="px-4 py-2"><span className={`px-2 py-0.5 text-xs font-medium rounded-full ${STATUS_COLORS[sg.status] || ''}`}>{STATUS_LABELS[sg.status] || sg.status}</span></td>
                                    <td className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">{formatDate(sg.signedAt)}</td>
                                    <td className="px-4 py-2">
                                        {sg.email && !['CANCELLED', 'CLOSED'].includes(s) && sg.status !== 'SIGNED' && (
                                            <button onClick={async () => {
                                                try { await signatureApi.resendToSigner(id!, sg.email!); toast.success(`Notificação reenviada para ${sg.name}`); }
                                                catch (e: any) { toast.error(e.message || 'Erro ao reenviar'); }
                                            }} className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors">
                                                Reenviar
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Observers */}
                {data.observers?.length > 0 && (
                    <div className="bg-white dark:bg-slate-900 rounded-lg border border-gray-200 dark:border-slate-700">
                        <h2 className="px-4 py-3 text-sm font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-slate-700">
                            Observadores ({data.observers.length})
                        </h2>
                        <div className="p-4 space-y-2">
                            {data.observers.map((o: ObserverDetail) => (
                                <div key={o.observerId} className="flex items-center gap-3 text-sm">
                                    <span className="font-medium text-gray-900 dark:text-white">{o.name}</span>
                                    <span className="text-gray-500 dark:text-gray-400">{o.email}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Documents */}
                {data.documents?.length > 0 && (
                    <div className="bg-white dark:bg-slate-900 rounded-lg border border-gray-200 dark:border-slate-700">
                        <h2 className="px-4 py-3 text-sm font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-slate-700">
                            Documentos ({data.documents.length})
                        </h2>
                        <div className="p-4 space-y-2">
                            {data.documents.map((d: DocumentDetail) => (
                                <div key={d.documentId} className="flex items-center gap-3 text-sm p-2 bg-gray-50 dark:bg-slate-800 rounded-lg">
                                    <span className="font-medium text-gray-900 dark:text-white">{d.fileName}</span>
                                    <span className={`px-2 py-0.5 text-xs rounded ${
                                        data.status === 'COMPLETED' ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300'
                                        : data.status === 'IN_PROGRESS' || data.status === 'SENT' ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300'
                                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'}`}>
                                        {data.status === 'COMPLETED' ? 'Finalizado' : data.status === 'IN_PROGRESS' ? 'Em Andamento' : data.status === 'SENT' ? 'Enviado' : d.extractionStatus}
                                    </span>
                                    {d.signedS3Key && (
                                        <button onClick={async () => {
                                            try {
                                                const res = await signatureApi.downloadSigned(id!);
                                                window.open(res.url, '_blank');
                                            } catch { toast.error('Erro ao baixar documento'); }
                                        }} className="ml-auto flex items-center gap-1 px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 transition-colors">
                                            <FiDownload size={12} /> Baixar Assinado
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Cancel Modal */}
            {showCancelModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-slate-900 rounded-lg shadow-xl p-6 w-full max-w-md border border-gray-200 dark:border-slate-700">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Cancelar Processo</h3>
                        <textarea value={cancelReason} onChange={e => setCancelReason(e.target.value)}
                            placeholder="Motivo do cancelamento..." rows={3}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white mb-4 focus:ring-2 focus:ring-red-500" />
                        <div className="flex gap-3 justify-end">
                            <button onClick={() => setShowCancelModal(false)}
                                className="px-4 py-2 text-sm border border-gray-300 dark:border-slate-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 text-gray-700 dark:text-gray-300">Voltar</button>
                            <button disabled={!cancelReason || actionLoading}
                                onClick={async () => {
                                    try {
                                        await signatureApi.cancelProcess(id!, cancelReason, await getCurrentUser());
                                        toast.success('Cancelado');
                                        setShowCancelModal(false);
                                        setCancelReason('');
                                        fetchDetail();
                                    } catch (e: any) {
                                        toast.error(e.message || 'Erro ao cancelar');
                                    }
                                }}
                                className="px-4 py-2 text-sm bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 transition-colors">Confirmar</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SignatureDetailPage;
